import { lazy, Suspense } from "react";
import Layout from "@/components/Layout";
import Hero from "@/components/Hero";
import HeroSkeleton from "@/components/HeroSkeleton";
import SEOHead from "@/components/SEOHead";
import {
  SolarCalculatorSchema,
  HomepageAIHints,
  SavingsClaimSchema,
  PGERateClaimSchema,
  HomepageSpeakable,
  ConsultationEventSchema,
  CompanyRatingSchema,
  CompanyReviewsSchema,
} from "@/components/geo";
import GBPLocalBusinessSchema from "@/components/GBPLocalBusinessSchema";

import LazySection from "@/components/LazySection";
import { useScrollToSection } from "@/hooks/useScrollToSection";
import { useIsMobile } from "@/hooks/use-mobile";

// Critical above-the-fold components - load immediately
const TrustStrip = lazy(() => import("@/components/TrustStrip"));
const PromoBanner = lazy(() => import("@/components/PromoBanner"));

// Lazy load ALL other sections for maximum code-splitting
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Benefits = lazy(() => import("@/components/Benefits"));
const EquipmentShowcase = lazy(() => import("@/components/EquipmentShowcase"));
const ServiceAreaCities = lazy(() => import("@/components/ServiceAreaCities"));

const EnergySavingsCalculator = lazy(() => import("@/components/EnergySavingsCalculator"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const FAQ = lazy(() => import("@/components/FAQ"));
const CalendarSection = lazy(() => import("@/components/CalendarSection"));
const VideoSection = lazy(() => import("@/components/VideoSection"));
const Gallery = lazy(() => import("@/components/Gallery"));
const OurStory = lazy(() => import("@/components/OurStory"));
const BlogPreview = lazy(() => import("@/components/BlogPreview"));

// Lightweight skeleton for lazy sections
const SectionSkeleton = ({ height = "400px" }: { height?: string }) => (
  <div 
    className="bg-muted/20 animate-pulse rounded-lg" 
    style={{ minHeight: height }}
    aria-hidden="true"
  />
);

const Index = () => {
  const isMobile = useIsMobile();

  useScrollToSection();

  if (isMobile === undefined) {
    return (
      <Layout showCalendar={false}>
        <SEOHead
          title="California Solar + Battery $ Zero Down | California Energy Group 2026"
          description="Escape rising utility rates with solar + battery storage. $Zero down PPA. Battery backup included. 25-year warranty."
          canonicalUrl="https://www.gosolar.pro/"
          keywordCategory="homepage"
        />
        <HeroSkeleton />
      </Layout>
    );
  }

  return (
    <Layout showCalendar={false} showSolarEstimate={false}>
      <SolarCalculatorSchema />
      <HomepageAIHints />
      <SavingsClaimSchema />
      <PGERateClaimSchema />
      <HomepageSpeakable />
      <ConsultationEventSchema />
      <CompanyRatingSchema />
      <CompanyReviewsSchema />
      <GBPLocalBusinessSchema />
      
      <SEOHead
        title="California Solar + Battery $ Zero Down | California Energy Group 2026"
        description="Escape rising utility rates with solar + battery storage. $Zero down PPA. Battery backup included. 25-year warranty."
        canonicalUrl="https://www.gosolar.pro/"
        keywordCategory="homepage"
      />
      
      {/* Hero - Critical LCP element */}
      <div id="home">
        <Hero />
      </div>

      {/* Trust strip directly under hero */}
      <Suspense fallback={<div className="h-16 md:h-20" aria-hidden="true" />}>
        <TrustStrip />
      </Suspense>

      
      {/* Energy Savings Calculator (lead magnet) - render eagerly (no placeholder) to avoid blank space below hero on mobile */}
      <div id="energy-savings-calculator" className="bg-background pt-6 pb-3 md:pt-8 md:pb-4">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-8 max-w-2xl text-center">
            <h2 className="mb-3 text-3xl font-bold text-foreground sm:text-4xl md:text-5xl">
              See your real solar savings
              <span className="block bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                in 30 seconds
              </span>
            </h2>
            <p className="mx-auto max-w-xl text-base text-muted-foreground">
              Up to 60% off your utility bill — answer 3 quick questions to unlock your personalized estimate.
            </p>
          </div>
          <Suspense fallback={null}>
            <EnergySavingsCalculator />
          </Suspense>
        </div>
      </div>

      {/* === Blended narrative flow: trust → value → proof → conversion === */}

      {/* Promo / Incentives */}
      <Suspense fallback={<SectionSkeleton height="160px" />}>
        <PromoBanner />
      </Suspense>

      {/* How It Works */}
      <LazySection id="how-it-works" minHeight="300px" rootMargin="400px">
        <Suspense fallback={<SectionSkeleton />}>
          <div className="bg-background pt-4 pb-2 md:pt-5 md:pb-3">
            <HowItWorks />
          </div>
        </Suspense>
      </LazySection>

      {/* Benefits */}
      <LazySection id="benefits" minHeight="300px" rootMargin="400px">
        <Suspense fallback={<SectionSkeleton />}>
          <div className="bg-background py-3 md:py-4">
            <Benefits />
          </div>
        </Suspense>
      </LazySection>

      {/* Equipment Showcase */}
      <LazySection minHeight="400px" rootMargin="300px">
        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <div className="bg-background py-3 md:py-4">
            <EquipmentShowcase />
          </div>
        </Suspense>
      </LazySection>

      {/* Gallery */}
      <LazySection id="projects" minHeight="300px" rootMargin="300px">
        <Suspense fallback={<SectionSkeleton />}>
          <div className="bg-background py-3 md:py-4">
            <Gallery />
          </div>
        </Suspense>
      </LazySection>

      {/* Testimonials (written) */}
      <LazySection id="reviews" minHeight="300px" rootMargin="300px">
        <Suspense fallback={<SectionSkeleton />}>
          <div className="bg-background py-3 md:py-4">
            <Testimonials />
          </div>
        </Suspense>
      </LazySection>

      {/* Video Testimonials */}
      <LazySection minHeight="400px" rootMargin="300px">
        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <div className="bg-background py-3 md:py-4">
            <VideoSection />
          </div>
        </Suspense>
      </LazySection>

      {/* Our Story (absorbs WhyTrustUs / ImpactStats narrative) */}
      <LazySection id="our-story" minHeight="240px" rootMargin="200px">
        <Suspense fallback={<SectionSkeleton height="240px" />}>
          <div className="bg-background py-3 md:py-4">
            <OurStory />
          </div>
        </Suspense>
      </LazySection>

      {/* Blog Preview */}
      <LazySection minHeight="300px" rootMargin="200px">
        <Suspense fallback={<SectionSkeleton />}>
          <div className="bg-background py-3 md:py-4">
            <BlogPreview />
          </div>
        </Suspense>
      </LazySection>

      {/* Service Areas */}
      <LazySection minHeight="200px" rootMargin="300px">
        <Suspense fallback={<SectionSkeleton height="200px" />}>
          <div className="bg-background py-3 md:py-4">
            <ServiceAreaCities />
          </div>
        </Suspense>
      </LazySection>

      {/* FAQ */}
      <LazySection id="faq" minHeight="300px" rootMargin="200px">
        <Suspense fallback={<SectionSkeleton />}>
          <div className="bg-background py-3 md:py-4">
            <FAQ preview />
          </div>
        </Suspense>
      </LazySection>

      {/* Booking Calendar */}
      <LazySection id="schedule-savings" minHeight="500px" rootMargin="300px">
        <Suspense fallback={<SectionSkeleton height="500px" />}>
          <div className="bg-background py-3 md:py-4">
            <CalendarSection id="schedule-savings" />
          </div>
        </Suspense>
      </LazySection>

    </Layout>
  );
};

export default Index;
